import { createContext, useContext } from "react";
import { trpc } from "../trpc";

type Ruleset = {
  id: string;
  name: string;
  assignedEmployees?: string[];
  ruleset: {
    zones?: string[];
    price?: { min?: number; max?: number };
    department?: "rent" | "sale";
    daysUntilMoveIn?: { min?: number; max?: number };
    projectIds?: string[];
    appointmentType?: "in-person" | "virtual";
    branchName?: string;
    employeeId?: string;
    propertyTypes?: string[];
    transferCategories?: string[];
  };
};

type RulesetContextType = {
  rulesets: Ruleset[];
  getRulesetName: (rulesetId: string) => string | undefined;
  isLoading: boolean;
};

const RulesetContext = createContext<RulesetContextType>({
  rulesets: [],
  getRulesetName: () => undefined,
  isLoading: true,
});

export const useRulesetContext = () => {
  const context = useContext(RulesetContext);
  if (!context) {
    throw new Error("useRulesetContext must be used within a RulesetContextProvider");
  }
  return context;
};

export const RulesetContextProvider = ({
  children,
  merchantId,
}: React.PropsWithChildren<{ merchantId: string }>): JSX.Element => {
  const { data: rulesets = [], isLoading } = trpc.assignmentRulesets.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );

  const getRulesetName = (rulesetId: string): string | undefined => {
    return rulesets.find((ruleset) => ruleset.id === rulesetId)?.name;
  };

  return (
    <RulesetContext.Provider
      value={{
        rulesets,
        getRulesetName,
        isLoading,
      }}
    >
      {children}
    </RulesetContext.Provider>
  );
};

export default RulesetContext;
